import { useQuery } from '@tanstack/react-query';
import type { CalendarItemDto, EventDto } from '@cw/shared';
import { api } from './api';

export const calendarKeys = {
  all: ['calendar'] as const,
  range: (qs: string) => ['calendar', qs] as const,
  event: (id: string) => ['calendar', 'event', id] as const,
};

export type CalendarFilters = {
  projectId?: string;
  clientId?: string;
  userId?: string;
  due: 'internal' | 'client' | 'both';
  kinds?: string;
};

export function useCalendar(from: Date, to: Date, f: CalendarFilters) {
  const p = new URLSearchParams({ from: from.toISOString(), to: to.toISOString(), due: f.due });
  if (f.projectId) p.set('projectId', f.projectId);
  if (f.clientId) p.set('clientId', f.clientId);
  if (f.userId) p.set('userId', f.userId);
  if (f.kinds) p.set('kinds', f.kinds);
  const qs = p.toString();
  return useQuery({
    queryKey: calendarKeys.range(qs),
    queryFn: () => api.get<CalendarItemDto[]>(`/calendar?${qs}`),
    placeholderData: (prev) => prev,
  });
}

export function useEvent(id: string | null) {
  return useQuery({
    queryKey: calendarKeys.event(id ?? ''),
    queryFn: () => api.get<EventDto>(`/events/${id}`),
    enabled: Boolean(id),
  });
}

// Datas (calendário local, semana começando na segunda) -------------------------

export const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
export const addDays = (d: Date, n: number) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, d.getHours(), d.getMinutes());
export const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
export const startOfWeek = (d: Date) => {
  const s = startOfDay(d);
  return addDays(s, -((s.getDay() + 6) % 7));
};
export const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
export const minutesOf = (d: Date) => d.getHours() * 60 + d.getMinutes();
export const fmtHM = (d: Date) => d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

/** Valor para <input type="datetime-local"> no fuso local. */
export function toLocalInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
